import React from 'react';
import { Capacitor } from '@capacitor/core';

function buildFallbackDebugBundle(error, errorInfo) {
  let cachedUser = null;
  try {
    const raw = localStorage.getItem('gweffit_user_cache');
    cachedUser = raw ? JSON.parse(raw) : null;
  } catch {}

  return {
    timestamp: new Date().toISOString(),
    platform: typeof Capacitor?.getPlatform === 'function' ? Capacitor.getPlatform() : 'web',
    href: typeof window !== 'undefined' ? window.location.href : null,
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    auth_user_id: cachedUser?.auth_user_id || null,
    app_user_id: cachedUser?.id || null,
    error: {
      message: error?.message || String(error || 'unknown'),
      stack: error?.stack || null,
      component_stack: errorInfo?.componentStack || null,
    },
  };
}

export default class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ error, errorInfo });
    console.error('[app-error-boundary] uncaught', error, errorInfo);
  }

  handleCopyDebugBundle = async () => {
    try {
      const payload = buildFallbackDebugBundle(this.state.error, this.state.errorInfo);
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
      alert('Debug bundle copied.');
    } catch (err) {
      alert(`Could not copy debug bundle: ${err?.message || String(err)}`);
    }
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="min-h-screen bg-slate-900 text-slate-100 flex items-center justify-center p-6">
        <div className="max-w-md w-full rounded-2xl border border-slate-700 bg-slate-800/80 p-5">
          <div className="text-xl font-black">Something went wrong</div>
          <div className="mt-2 text-sm text-slate-300">
            The app hit an unexpected error. You can reload, or copy a debug bundle to share.
          </div>
          <div className="mt-4 flex gap-2">
            <button
              onClick={() => window.location.reload()}
              className="flex-1 rounded-lg bg-purple-600 hover:bg-purple-500 text-white py-2 text-sm font-bold"
            >
              Reload
            </button>
            <button
              onClick={this.handleCopyDebugBundle}
              className="flex-1 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-100 py-2 text-sm font-bold"
            >
              Copy debug bundle
            </button>
          </div>
        </div>
      </div>
    );
  }
}
